import React, { useState } from "react";
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Chip,
  Button,
  Grid,
  Paper,
  TextField,
  IconButton,
  Tooltip,
  Divider,
} from "@mui/material";
import { QRCodeCanvas } from "qrcode.react";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  ContentCopy as CopyIcon,
  WhatsApp as WhatsAppIcon,
  Download as DownloadIcon,
} from "@mui/icons-material";
import {
  getInvitationStatusDisplay,
  buildWhatsappShareLink,
  formatDate,
} from "../utils/invitationHelpers";
import InvitationImageUpload from "./InvitationImageUpload";

const InvitationManager = ({
  invitation,
  onEdit,
  onDelete,
  onImageUpload,
  uploadingKey,
  onPublish,
  onUnpublish,
}) => {
  const [tab, setTab] = useState(0);
  const statusDisplay = getInvitationStatusDisplay(invitation.status);

  const handleCopyLink = () => {
    navigator.clipboard.writeText(invitation.share?.invitationUrl || "");
  };

  const handleDownloadQr = () => {
    const canvas = document.getElementById("invitation-qr-code");
    if (canvas) {
      const url = canvas.toDataURL("image/png");
      const a = document.createElement("a");
      a.href = url;
      a.download = `${invitation.invitationCode}-qr.png`;
      a.click();
    }
  };

  return (
    <Box>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
          }}
        >
          <Box>
            <Typography variant="h5" fontWeight="bold">
              {invitation.invitationDetails?.title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {invitation.invitationCode} •{" "}
              {invitation.invitationDetails?.groomName} &{" "}
              {invitation.invitationDetails?.brideName}
            </Typography>
          </Box>
          <Chip label={statusDisplay.label} color={statusDisplay.color} />
        </Box>
        <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={onEdit}
          >
            Edit
          </Button>
          {invitation.status === "published" ? (
            <Button
              size="small"
              variant="outlined"
              color="warning"
              onClick={onUnpublish}
            >
              Unpublish
            </Button>
          ) : (
            <Button
              size="small"
              variant="contained"
              color="success"
              onClick={onPublish}
            >
              Publish
            </Button>
          )}
          <Button
            size="small"
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={onDelete}
          >
            Delete
          </Button>
        </Box>
      </Paper>

      <Tabs
        value={tab}
        onChange={(e, v) => setTab(v)}
        sx={{ mb: 2 }}
        variant="scrollable"
      >
        <Tab label="Details" />
        <Tab label="Images" />
        <Tab label="Share & QR" />
      </Tabs>

      {tab === 0 && (
        <Paper sx={{ p: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={4}>
              <Typography color="text.secondary">Event Date</Typography>
            </Grid>
            <Grid item xs={6} sm={8}>
              <Typography>
                {formatDate(invitation.invitationDetails?.eventDate)}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={4}>
              <Typography color="text.secondary">Start Time</Typography>
            </Grid>
            <Grid item xs={6} sm={8}>
              <Typography>
                {invitation.invitationDetails?.startTime || "N/A"}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={4}>
              <Typography color="text.secondary">Venue</Typography>
            </Grid>
            <Grid item xs={6} sm={8}>
              <Typography>
                {invitation.invitationDetails?.venueName || "N/A"}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={4}>
              <Typography color="text.secondary">Address</Typography>
            </Grid>
            <Grid item xs={6} sm={8}>
              <Typography>
                {invitation.invitationDetails?.venueAddress || "N/A"}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={4}>
              <Typography color="text.secondary">Template</Typography>
            </Grid>
            <Grid item xs={6} sm={8}>
              <Typography>{invitation.template?.templateName}</Typography>
            </Grid>
            <Grid item xs={6} sm={4}>
              <Typography color="text.secondary">Message</Typography>
            </Grid>
            <Grid item xs={6} sm={8}>
              <Typography>
                {invitation.invitationDetails?.invitationMessage || "N/A"}
              </Typography>
            </Grid>
          </Grid>
        </Paper>
      )}

      {tab === 1 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Invitation Images
          </Typography>
          <InvitationImageUpload
            images={invitation.images || {}}
            onUpload={onImageUpload}
            uploadingKey={uploadingKey}
          />
        </Paper>
      )}

      {tab === 2 && (
        <Paper sx={{ p: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Share Link
              </Typography>
              <TextField
                fullWidth
                size="small"
                value={invitation.share?.invitationUrl || ""}
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <Tooltip title="Copy link">
                      <IconButton size="small" onClick={handleCopyLink}>
                        <CopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  ),
                }}
              />
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 1, display: "block" }}
              >
                Short URL: {invitation.share?.shortUrl}
              </Typography>

              <Button
                variant="contained"
                color="success"
                startIcon={<WhatsAppIcon />}
                fullWidth
                sx={{ mt: 2 }}
                href={buildWhatsappShareLink(
                  invitation.share?.whatsappMessage,
                  invitation.share?.invitationUrl,
                )}
                target="_blank"
                rel="noopener noreferrer"
              >
                Share on WhatsApp
              </Button>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                QR Code
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 2,
                }}
              >
                <Paper
                  variant="outlined"
                  sx={{ p: 2, display: "inline-block" }}
                >
                  <QRCodeCanvas
                    id="invitation-qr-code"
                    value={invitation.share?.invitationUrl || ""}
                    size={180}
                    fgColor={invitation.template?.primaryColor || "#000000"}
                    level="H"
                    includeMargin
                  />
                </Paper>
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={handleDownloadQr}
                >
                  Download QR Code
                </Button>
              </Box>
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" sx={{ mb: 1 }}>
            WhatsApp Message
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {invitation.share?.whatsappMessage}
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default InvitationManager;
